import type { HTMLAttributes, ReactNode, ThHTMLAttributes, TdHTMLAttributes, TableHTMLAttributes } from 'react';

interface TableProps extends Omit<TableHTMLAttributes<HTMLTableElement>, 'className'> {
  readonly className?: string;
  /** Use admin (denser) padding via .adm-tbl */
  readonly dense?: boolean;
  /**
   * Phone layout (≤720px): every row becomes a stacked card and every cell a
   * "LABEL … value" line (label from the cell's `data-label` / TD `label`).
   * Adds `.tbl-stack`; the desktop table is unchanged. See styles.css §10.
   */
  readonly stack?: boolean;
  /**
   * Wrap the table in a contained horizontal scroller (`.tbl-scroll`) with an
   * edge-shadow affordance — for matrix-like tables that must keep columns.
   * Off by default, so the default DOM (table as the direct child) is unchanged.
   */
  readonly scroll?: boolean;
  readonly children?: ReactNode;
}

export function Table({ className, dense, stack, scroll, children, ...rest }: TableProps): JSX.Element {
  const cls = ['tbl', dense ? 'adm-tbl' : '', stack ? 'tbl-stack' : '', className ?? ''].filter(Boolean).join(' ');
  const table = (
    <table className={cls} {...rest}>
      {children}
    </table>
  );
  return scroll ? <div className="tbl-scroll">{table}</div> : table;
}

interface RowProps extends Omit<HTMLAttributes<HTMLTableRowElement>, 'className'> {
  readonly className?: string;
  readonly clickable?: boolean;
  readonly children?: ReactNode;
}

export function TRow({ clickable, className, children, ...rest }: RowProps): JSX.Element {
  const cls = [clickable ? 'clickable' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <tr className={cls} {...rest}>
      {children}
    </tr>
  );
}

export function THead({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <thead {...rest}>{children}</thead>;
}

export function TBody({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <tbody {...rest}>{children}</tbody>;
}

export function TH({ children, ...rest }: ThHTMLAttributes<HTMLTableCellElement>): JSX.Element {
  return <th {...rest}>{children}</th>;
}

interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /**
   * Column label shown in front of the value when the table is stacked on a
   * phone (`<Table stack>` / `.tbl-stack`). Rendered as `data-label`; an
   * explicit `data-label` attribute works the same way.
   */
  readonly label?: string;
}

export function TD({ children, label, ...rest }: TDProps): JSX.Element {
  return (
    <td data-label={label} {...rest}>
      {children}
    </td>
  );
}
